import { Services } from '../../types/DealerServices';
import {
  MARKET_IMPACT_PERMISSIONS,
  MARKET_IMPACT_TYPES,
  MarketImpactAvailableService,
  MarketImpactServicesMode,
  MarketImpactType,
} from '../../types/MarketImpact';
import { useCommon } from '../common/UseCommon';

export const useMarketImpact = (): MarketImpactTypes => {
  const { checkForPermission } = useCommon();
  const getMarketImpactServicesMode = (services: Services): MarketImpactServicesMode => ({
    new: services?.hasMarketImpactNewVehicles,
    used: services?.hasMarketImpactPreowned,
  });

  // Checks for specific Market Impact service availability.
  const isMarketImpactServiceAvailable = (
    marketImpactType: MarketImpactType,
    isServiceActive: boolean,
    isInternalUser: boolean | undefined,
    permissions: string[],
  ): boolean => {
    if (isInternalUser || isInternalUser === undefined) {
      return false;
    }
    const servicePermission = MARKET_IMPACT_PERMISSIONS[marketImpactType];
    const hasPermission = checkForPermission(permissions, servicePermission);
    return isServiceActive && hasPermission;
  };

  // Get available Market Impact Services for the current user.
  const getAvailableMarketImpactServices = (
    marketImpactServicesMode: MarketImpactServicesMode,
    isInternalUser: boolean,
    permissions: string[],
  ) =>
    MARKET_IMPACT_TYPES.map<MarketImpactAvailableService>((marketImpactType) => {
      const isServiceActive = marketImpactServicesMode[marketImpactType];
      return {
        type: marketImpactType,
        isAvailable: isMarketImpactServiceAvailable(
          marketImpactType,
          isServiceActive,
          isInternalUser,
          permissions,
        ),
      };
    }).filter(({ isAvailable }) => isAvailable);

  return {
    getMarketImpactServicesMode,
    getAvailableMarketImpactServices,
    isMarketImpactServiceAvailable,
  };
};

type MarketImpactTypes = {
  getMarketImpactServicesMode(services: Services): MarketImpactServicesMode;
  getAvailableMarketImpactServices(
    marketImpactServicesMode: MarketImpactServicesMode,
    isInternalUser: boolean,
    permissions: string[],
  ): MarketImpactAvailableService[];
  isMarketImpactServiceAvailable(
    marketImpactType: MarketImpactType,
    isServiceActive: boolean,
    isInternalUser: boolean | undefined,
    permissions: string[],
  ): boolean;
};
